import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, ReloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { servicesService } from '../../services';
import { VMService, ServiceStatus } from '../../types/service.type';
import { getServiceAge } from '../../utils/serviceHelpers';
import VMCard from './components/VMCard';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import { useTranslation } from 'react-i18next';

interface VirtualMachine {
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'deploying' | 'error';
  realStatus: string;
  os: string;
  osImage?: string;
  instanceType: string;
  region: string;
  ipAddress?: string;
  createdAt: string;
}

const isVMServiceType = (type: string | undefined): boolean => {
  if (!type) return false;
  const normalizedType = type.toLowerCase();
  return normalizedType === 'vm' || normalizedType === 'compute' || normalizedType === 'virtual-machine';
};

const getOSIcon = (osFamily: string | undefined, productImages?: string[]): string | undefined => {
  if (productImages && productImages.length > 0) return productImages[0];
  if (!osFamily) return undefined;
  const logoMap: Record<string, string> = {
    'amazon-linux': '/images/amazon linux.png',
    'ubuntu-22.04': '/images/ubuntu.png',
    'ubuntu-20.04': '/images/ubuntu.png',
    'ubuntu': '/images/ubuntu.png',
    'rhel': '/images/linux.png',
    'redhat': '/images/linux.png',
    'red hat': '/images/linux.png',
    'linux': '/images/linux.png',
    'debian': '/images/debian.png',
    'centos': '/images/linux.png',
    'suse': '/images/suse.png',
    'sles': '/images/suse.png',
    'opensuse': '/images/suse.png',
    'windows': '/images/windows.png',
  };
  const osFamilyLower = osFamily.toLowerCase();
  for (const [key, logoUrl] of Object.entries(logoMap)) {
    if (osFamilyLower.includes(key.toLowerCase())) return logoUrl;
  }
  return undefined;
};

const VirtualMachines: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<ServiceStatus | 'all'>('all');
  const [virtualMachines, setVirtualMachines] = useState<VirtualMachine[]>([]);
  const [loading, setLoading] = useState(true);

  const mapVMServiceToVirtualMachine = useCallback((vmService: any): VirtualMachine => {
    const mapStatus = (status: string): 'running' | 'stopped' | 'deploying' | 'error' => {
      switch (status?.toLowerCase()) {
        case 'running': return 'running';
        case 'stopped': return 'stopped';
        case 'deploying':
        case 'initializing':
        case 'stacking':
        case 'creating':
        case 'starting':
        case 'restarting': return 'deploying';
        case 'error':
        case 'terminated': return 'error';
        default: return 'stopped';
      }
    };
    const osFamily = vmService.osFamily;
    const osImage = getOSIcon(osFamily, vmService.product?.images);
    const osName =
      vmService.metadata?.vmConfig?.osName ||
      vmService.product?.os ||
      vmService.product?.osFamily ||
      osFamily ||
      vmService.os ||
      'Unknown OS';
    return {
      id: vmService.id,
      name: vmService.name,
      status: mapStatus(vmService.status),
      realStatus: vmService.status || 'unknown',
      os: osName,
      osImage,
      instanceType: vmService.instanceType || vmService.metadata?.vmConfig?.instanceType || 'N/A',
      region: vmService.region || 'N/A',
      ipAddress: vmService.ipAddress,
      createdAt: getServiceAge(vmService.createdAt),
    };
  }, []);

  useEffect(() => {
    const fetchVMs = async () => {
      try {
        setLoading(true);
        const response = await servicesService.getByCategory('VM');
        const mappedVMs = response.services
          .filter((service): service is VMService => isVMServiceType(service.type))
          .map(mapVMServiceToVirtualMachine);
        setVirtualMachines(mappedVMs);
      } catch {
        setVirtualMachines([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVMs();
  }, [mapVMServiceToVirtualMachine]);

  const refreshVMs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await servicesService.getByCategory('VM');
      const mappedVMs = response.services
        .filter((service): service is VMService => isVMServiceType(service.type))
        .map(mapVMServiceToVirtualMachine);
      setVirtualMachines(mappedVMs);
    } catch {
      // keep existing
    } finally {
      setLoading(false);
    }
  }, [mapVMServiceToVirtualMachine]);

  const filteredVMs = virtualMachines.filter((vm) => {
    const matchesSearch =
      vm.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vm.os?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vm.instanceType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vm.region?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vm.ipAddress && vm.ipAddress.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus =
      filterStatus === 'all' ||
      (vm.realStatus && vm.realStatus.toLowerCase() === filterStatus.toLowerCase()) ||
      vm.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const STATUS_PILLS = [
    { value: 'all', label: t('common.all') || 'Todas' },
    { value: 'running', label: t('virtualMachines.list.running') },
    { value: 'stopped', label: t('virtualMachines.list.stopped') },
    { value: 'deploying', label: t('virtualMachines.list.deploying') },
    { value: 'error', label: t('virtualMachines.list.error') || 'Error' },
  ];

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-4">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('virtualMachines.list.title')}
          </h1>
          {!loading && (
            <span className="text-sm text-gray-400 dark:text-gray-500">{filteredVMs.length}</span>
          )}
        </div>

        <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
          {/* Search */}
          <div className="relative">
            <SearchOutlined className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
            <input
              type="text"
              placeholder={t('virtualMachines.list.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-900 dark:focus:ring-gray-400 w-44"
            />
          </div>

          {/* Status pills */}
          <div className="flex items-center gap-1">
            {STATUS_PILLS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setFilterStatus(value as ServiceStatus | 'all')}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  filterStatus === value
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-gray-900 dark:border-white'
                    : 'text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Refresh */}
          <button
            onClick={refreshVMs}
            disabled={loading}
            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-40"
            title={t('common.refresh')}
          >
            {loading ? <LoadingOutlined spin /> : <ReloadOutlined />}
          </button>

          {/* Create */}
          <button
            onClick={() => navigate('/resources/create-vm')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm rounded-md hover:bg-gray-700 dark:hover:bg-gray-100 transition-colors"
          >
            <PlusOutlined className="text-xs" />
            {t('virtualMachines.create.title')}
          </button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-20">
          <LoadingOutlined className="text-2xl text-gray-300 dark:text-gray-600" spin />
        </div>
      ) : filteredVMs.length === 0 ? (
        <EmptyState
          title={t('virtualMachines.list.noResults')}
          description={
            searchTerm || filterStatus !== 'all'
              ? t('virtualMachines.list.noResults')
              : t('virtualMachines.list.emptyState.description')
          }
          action={
            !searchTerm && filterStatus === 'all'
              ? { label: t('virtualMachines.list.emptyState.button'), onClick: () => navigate('/resources/create-vm') }
              : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVMs.map((vm) => (
            <VMCard
              key={vm.id}
              id={vm.id}
              name={vm.name}
              status={vm.status}
              realStatus={vm.realStatus}
              os={vm.os}
              osImage={vm.osImage}
              instanceType={vm.instanceType}
              region={vm.region}
              ipAddress={vm.ipAddress}
              createdAt={vm.createdAt}
              onRefresh={refreshVMs}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default VirtualMachines;
